/* Le sezioni del programma: il menu e le rotte, dalla stessa mappa.
 *
 * **Una mappa sola, non due.** La prima versione aveva l'elenco del menu qui
 * e l'elenco delle rotte in `App.tsx`, e le due cose divergevano subito: un
 * operatore che scriveva `/tariffe` a mano vedeva la pagina, perche' il menu
 * gliela nascondeva ma il router gliela serviva comunque, e avrebbe visto una
 * schermata che risponde 403 a ogni richiesta, cioe' qualcosa che sembra un
 * guasto del programma invece di un permesso che non ha.
 *
 * **Riorganizzata il 25 settembre 2026 per mestiere, non per pagina.** Le
 * otto voci nate una alla volta, tutte uguali in fila, non raccontavano il
 * flusso del programma: si imputa, si fattura, si controlla, ogni tanto si
 * configura. Ora sono quattro voci di lavoro e le impostazioni a parte, come
 * la voce "Manage" di Harvest: catalogo, tariffe e utenti si toccano una
 * volta al mese e non devono stare sullo stesso piano delle ore.
 *
 * **Questo non e' il permesso.** Il permesso vero sta in `core/capacita.py` e
 * lo fa rispettare il backend a ogni richiesta: qui si decide solo cosa ha
 * senso mostrare. Se un giorno le due cose divergono, quella giusta e'
 * sempre l'altra.
 */

import type { ReactNode } from "react";
import type { Ruolo } from "./api";
import { Catalogo } from "./pagine/Catalogo";
import { Clienti } from "./pagine/Clienti";
import { Costi } from "./pagine/Costi";
import { Cruscotto } from "./pagine/Cruscotto";
import { Documenti } from "./pagine/Documenti";
import { Gestione } from "./pagine/Gestione";
import { Ore } from "./pagine/Ore";
import { Periodo } from "./pagine/Periodo";
import { Rapporti } from "./pagine/Rapporti";
import { RegistroOre } from "./pagine/RegistroOre";
import { Tariffe } from "./pagine/Tariffe";
import { Utenti } from "./pagine/Utenti";

export interface Scheda {
  percorso: string;
  nome: string;
  ruoli: Ruolo[];
  elemento: ReactNode;
}

export interface Sezione {
  percorso: string;
  nome: string;
  ruoli: Ruolo[];
  /** Una pagina sola, che puo' leggere da se' il resto dell'indirizzo. */
  elemento?: ReactNode;
  /** Oppure piu' pagine sotto la stessa voce, come schede. */
  schede?: Scheda[];
  /** Staccata a destra e piu' tenue: le impostazioni. */
  laterale?: boolean;
}

const TUTTI: Ruolo[] = ["AMMINISTRATORE", "FATTURAZIONE", "OPERATORE"];
const CHI_FATTURA: Ruolo[] = ["AMMINISTRATORE", "FATTURAZIONE"];
const TITOLARE: Ruolo[] = ["AMMINISTRATORE"];

/* I ruoli si elencano per esteso invece di dedurli. Un elenco sbagliato qui
 * nasconde una voce a chi ne ha diritto, e quella persona lo dice il giorno
 * stesso; una deduzione sbagliata mostrerebbe voci che poi rispondono 403. */
const SEZIONI: Sezione[] = [
  {
    percorso: "/ore",
    nome: "Ore",
    ruoli: TUTTI,
    // Il giorno per scrivere, il registro per controllare: due gesti diversi,
    // due schede. Nel registro chi imputa vede solo le sue ore (lo decide il
    // server), chi fattura quelle di tutti.
    schede: [
      { percorso: "giornata", nome: "La mia giornata", ruoli: TUTTI, elemento: <Ore /> },
      { percorso: "registro", nome: "Registro", ruoli: TUTTI, elemento: <RegistroOre /> },
    ],
  },
  { percorso: "/fatturare", nome: "Fatturare", ruoli: CHI_FATTURA, elemento: <Documenti /> },
  { percorso: "/clienti", nome: "Clienti", ruoli: CHI_FATTURA, elemento: <Clienti /> },
  {
    percorso: "/controllo",
    nome: "Controllo",
    ruoli: CHI_FATTURA,
    schede: [
      { percorso: "cruscotto", nome: "Cruscotto", ruoli: CHI_FATTURA, elemento: <Cruscotto /> },
      // Dal 17 ottobre 2026: lavoro, avvisi, incassato e i due buchi per un periodo a scelta. Dal 23 ottobre
      // anche a chi fattura: sono soldi dei clienti. Le righe per persona le toglie il server (CONTROLLO_PERIODO).
      { percorso: "periodo", nome: "Periodo", ruoli: CHI_FATTURA, elemento: <Periodo /> },
      { percorso: "gestione", nome: "Gestione", ruoli: TITOLARE, elemento: <Gestione /> },
      { percorso: "raccolta", nome: "Raccolta delle ore", ruoli: TITOLARE, elemento: <Rapporti /> },
    ],
  },
  {
    percorso: "/impostazioni",
    nome: "Impostazioni",
    // Dal 16 ottobre 2026 catalogo e tariffe li tiene anche chi fattura (core/capacita.py): una
    // prestazione nuova o una tariffa che cambia non devono richiedere una telefonata ai titolari.
    // Utenti e costi orari restano del solo titolare.
    ruoli: CHI_FATTURA,
    laterale: true,
    schede: [
      { percorso: "catalogo", nome: "Catalogo", ruoli: CHI_FATTURA, elemento: <Catalogo /> },
      { percorso: "tariffe", nome: "Tariffe", ruoli: CHI_FATTURA, elemento: <Tariffe /> },
      { percorso: "utenti", nome: "Utenti", ruoli: TITOLARE, elemento: <Utenti /> },
      { percorso: "costi", nome: "Costi orari", ruoli: TITOLARE, elemento: <Costi /> },
    ],
  },
];

export function sezioniPer(ruolo: Ruolo): Sezione[] {
  return SEZIONI.filter((s) => s.ruoli.includes(ruolo)).map((s) =>
    s.schede ? { ...s, schede: s.schede.filter((c) => c.ruoli.includes(ruolo)) } : s,
  );
}

/** Da dove parte ognuno: dal suo lavoro, non dalla prima voce del menu. Chi
 * imputa dalle ore, chi fattura dalla sua coda, il titolare dal controllo. */
const CASA: Record<Ruolo, string> = {
  OPERATORE: "/ore",
  FATTURAZIONE: "/fatturare",
  AMMINISTRATORE: "/controllo",
};

export function primaSezione(ruolo: Ruolo): string {
  return CASA[ruolo] ?? sezioniPer(ruolo)[0]?.percorso ?? "/password";
}

/** Gli indirizzi di prima della riorganizzazione, che portano ai nuovi: un
 * segnalibro vecchio non deve finire su una pagina vuota. */
export const INDIRIZZI_VECCHI: Record<string, string> = {
  "/documenti": "/fatturare",
  "/cruscotto": "/controllo/cruscotto",
  "/rapporti": "/controllo/raccolta",
  "/catalogo": "/impostazioni/catalogo",
  "/tariffe": "/impostazioni/tariffe",
  "/utenti": "/impostazioni/utenti",
};
